<script lang="ts">
  /**
   * The wordmark, in the two places the name is shown as a heading.
   *
   * A component rather than two `<img>` tags so the asset is imported once and both places
   * move together. It is **not** the app icon — `icon-192.png` is a separate, square thing for
   * the launcher and the notifications, and the two must not be conflated.
   *
   * Always inside an `<h1>` at the call site, with the name in `alt`: a logo that replaces a
   * heading has to keep being a heading, or the page loses its only top-level label and a
   * screen reader announces an image where the app's name used to be.
   *
   * It comes alive in beats: the woman kicks her feet and blows a kiss, the pins on its map send
   * out a ping, the way a hotspot pulses on the real one, a glint runs across the letters, the
   * fire in the "o" flares and a few sparks go off. One beat every ten seconds rather than a
   * loop — in between nothing on it moves, so the phone draws nothing for it, which is the
   * choice the swarm pulse made too (amendment 14). With reduced motion it stands still.
   *
   * The picture is one image, so her lower legs come as a layer of their own, cut out of it by
   * `scripts/split-wordmark.py`; together the two layers are the original. Everything else is
   * laid over the picture.
   */
  import body from './wordmark-body.webp';
  import legs from './wordmark-legs.webp';

  type Props = {
    /** Rendered height. The width follows from the aspect ratio and is never set. */
    height: string;
  };

  const { height }: Props = $props();

  const BEAT_MS = 10_000;
  /** The first beat comes once the page has settled, not in the middle of it appearing. */
  const FIRST_MS = 1_400;

  /**
   * Where things are on the picture, in per cent of its box: the glow under each pin (with its
   * size), and the places on the letters a spark may go off.
   */
  const PINS = [
    { x: 58.4, y: 83.6, w: 13, h: 12.6, delay: 0 },
    { x: 72.8, y: 91.1, w: 5.5, h: 7.4, delay: 0.3 },
    { x: 88.4, y: 84, w: 5.5, h: 6.7, delay: 0.55 },
  ];
  const SPOTS = [
    { x: 7, y: 52 },
    { x: 24, y: 47 },
    { x: 33, y: 66 },
    { x: 43, y: 58 },
    { x: 51, y: 70 },
    { x: 77, y: 48 },
    { x: 84, y: 62 },
    { x: 94, y: 55 },
    { x: 30, y: 12 },
    { x: 62, y: 96 },
  ];
  const SPARKS = 3;

  /**
   * A kick of her feet: about the knee, back, forward, back, and still again. The easing is
   * each swing's own; one over the whole would never let the first swing reach its turn.
   */
  const KICK: Keyframe[] = [
    { transform: 'rotate(0deg)', easing: 'ease-in-out' },
    { transform: 'rotate(-9deg)', offset: 0.22, easing: 'ease-in-out' },
    { transform: 'rotate(6deg)', offset: 0.5, easing: 'ease-in-out' },
    { transform: 'rotate(-4deg)', offset: 0.76, easing: 'ease-in-out' },
    { transform: 'rotate(0deg)' },
  ];

  type Spark = { x: number; y: number; delay: number };

  let beat = $state(0);
  let sparks = $state<Spark[]>([]);
  let feet: HTMLImageElement | undefined = $state();

  function sparksForNow(): Spark[] {
    const spots = [...SPOTS].sort(() => Math.random() - 0.5).slice(0, SPARKS);
    return spots.map((spot, index) => ({ ...spot, delay: 0.15 + index * 0.25 }));
  }

  $effect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const go = () => {
      // Not while nobody sees it, and not for somebody who asked for stillness.
      if (document.visibilityState !== 'visible' || reduced.matches) return;
      sparks = sparksForNow();
      beat += 1;
      feet?.animate(KICK, 1400);
    };
    const first = setTimeout(go, FIRST_MS);
    const timer = setInterval(go, BEAT_MS);
    return () => {
      clearTimeout(first);
      clearInterval(timer);
    };
  });
</script>

<span class="mark" style:--logo={`url("${body}")`} style:--mark-height={height}>
  <span class="sticker">
    <img
      src={body}
      alt="hotspotter"
      width="800"
      height="269"
      style:height
      class="wordmark"
      draggable="false"
    />
    <img bind:this={feet} src={legs} alt="" class="legs" draggable="false" />
  </span>
  {#key beat}
    {#if beat > 0}
      <span class="beat" aria-hidden="true">
        <span class="glint"></span>
        <span class="glow fire"></span>
        <span class="glow alert"></span>
        <span class="kiss">❤️</span>
        <span class="kiss second">❤️</span>
        {#each PINS as pin, index (index)}
          {#each [0, 0.4] as later (later)}
            <span
              class="ring"
              style:left="{pin.x}%"
              style:top="{pin.y}%"
              style:width="{pin.w}%"
              style:height="{pin.h}%"
              style:--delay="{pin.delay + later}s"
            ></span>
          {/each}
        {/each}
        {#each sparks as spark, index (index)}
          <span
            class="spark"
            style:left="{spark.x}%"
            style:top="{spark.y}%"
            style:--delay="{spark.delay}s">✦</span
          >
        {/each}
      </span>
    {/if}
  {/key}
</span>

<style>
  .mark {
    position: relative;
    display: block;
    width: fit-content;
  }

  /* The two layers of the picture, shadowed and arriving as one. */
  .sticker {
    position: relative;
    display: block;
    /* The text this replaces carried a text-shadow so it stayed legible over the map. The
       equivalent for a transparent image is a drop-shadow, which follows the sticker outline
       rather than boxing it. */
    filter: drop-shadow(0 1px 4px var(--bg));
    animation: arrive 0.7s cubic-bezier(0.2, 1.5, 0.4, 1) both;
  }

  .wordmark {
    display: block;
    width: auto;
  }

  /* Her calves, turning about the knee where the script cut them, in per cent of the picture. */
  .legs {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    transform-origin: 15.5% 50.6%;
  }

  .beat {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  .beat > * {
    position: absolute;
    opacity: 0;
  }

  /* A band of light, cut to the sticker's own outline so that it never crosses the map. */
  .glint {
    inset: 0;
    overflow: hidden;
    opacity: 1;
    -webkit-mask: var(--logo) center / 100% 100% no-repeat;
    mask: var(--logo) center / 100% 100% no-repeat;
  }

  .glint::before {
    content: '';
    position: absolute;
    top: -10%;
    bottom: -10%;
    left: 0;
    width: 22%;
    background: linear-gradient(90deg, transparent, rgb(255 255 255 / 70%), transparent);
    transform: translateX(-130%) skewX(-20deg);
    animation: glint 1.2s ease-in-out 0.1s;
  }

  .glow {
    translate: -50% -50%;
    border-radius: 50%;
    mix-blend-mode: screen;
  }

  .fire {
    left: 18.75%;
    top: 73%;
    width: 7.5%;
    height: 22%;
    background: radial-gradient(
      closest-side,
      rgb(255 214 90 / 95%),
      rgb(255 100 0 / 55%) 55%,
      transparent
    );
    animation: flare 1.3s ease-out 0.2s;
  }

  /* The two marks above the big pin, as if it were calling out. */
  .alert {
    left: 64.8%;
    top: 36%;
    width: 9%;
    height: 30%;
    background: radial-gradient(closest-side, rgb(255 230 130 / 90%), transparent);
    animation: call 0.7s ease-out 0.25s 2;
  }

  /* Light against the pins' own red and yellow glow, or the ring is lost in it. */
  .ring {
    translate: -50% -50%;
    border: 2.5px solid rgb(255 244 200);
    border-radius: 50%;
    box-shadow: 0 0 6px rgb(255 190 60 / 80%);
    animation: ping 1.5s cubic-bezier(0.2, 0.6, 0.3, 1) var(--delay);
  }

  /* A kiss blown from her lips, drifting off over the letters. */
  .kiss {
    left: 38.75%;
    top: 29%;
    translate: -50% -50%;
    font-size: calc(var(--mark-height) * 0.22);
    line-height: 1;
    animation: kiss 1.8s ease-out 0.5s;
  }

  .kiss.second {
    font-size: calc(var(--mark-height) * 0.16);
    animation-delay: 0.85s;
  }

  .spark {
    translate: -50% -50%;
    color: #fff;
    font-size: calc(var(--mark-height) * 0.3);
    line-height: 1;
    text-shadow:
      0 0 4px #fff,
      0 0 10px rgb(255 200 80);
    animation: spark 0.9s ease-out var(--delay);
  }

  @keyframes arrive {
    from {
      opacity: 0;
      transform: translateY(-6px) scale(0.92);
    }
  }

  @keyframes glint {
    to {
      transform: translateX(560%) skewX(-20deg);
    }
  }

  @keyframes flare {
    0% {
      opacity: 0;
      transform: scale(0.6);
    }
    25% {
      opacity: 1;
      transform: scale(1.25);
    }
    45% {
      opacity: 0.55;
      transform: scale(1);
    }
    65% {
      opacity: 0.95;
      transform: scale(1.15);
    }
    100% {
      opacity: 0;
      transform: scale(0.9);
    }
  }

  @keyframes call {
    0% {
      opacity: 0;
    }
    30% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  @keyframes ping {
    0% {
      opacity: 1;
      transform: scale(0.8);
    }
    100% {
      opacity: 0;
      transform: scale(2.4);
    }
  }

  @keyframes kiss {
    0% {
      opacity: 0;
      transform: translate(0, 0) scale(0.2) rotate(-10deg);
    }
    15% {
      opacity: 1;
      transform: translate(calc(var(--mark-height) * 0.12), calc(var(--mark-height) * -0.08))
        scale(1) rotate(0deg);
    }
    100% {
      opacity: 0;
      transform: translate(calc(var(--mark-height) * 0.95), calc(var(--mark-height) * -0.4))
        scale(1.3) rotate(14deg);
    }
  }

  @keyframes spark {
    0% {
      opacity: 0;
      transform: scale(0) rotate(0deg);
    }
    40% {
      opacity: 1;
      transform: scale(1.1) rotate(45deg);
    }
    100% {
      opacity: 0;
      transform: scale(0.3) rotate(120deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .sticker {
      animation: none;
    }
  }
</style>
